import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import { HttpResponse, IApiGateway, TYPES } from '@desygner/ui-core-api-gateway';

import { GetMembershipArgs, InviteMemberBody } from './membership.type';

export interface IMembershipService {
  getMemberships(args?: GetMembershipArgs): Promise<HttpResponse>;
  getMembershipsCount(): Promise<HttpResponse>;
  inviteMember(body?: InviteMemberBody): Promise<HttpResponse[]>;
  resendInvite(id: string, invited_at?: string): Promise<HttpResponse>;
  validateInvitationToken(email: string, token: string): Promise<HttpResponse>;
  removeMember(id: string): Promise<HttpResponse>;
  updateMemberRole(id: string, access_level: string): Promise<HttpResponse>;
  removeCollaborationFromProject(id: string, project_id: string): Promise<HttpResponse>;
}

@injectable()
export class MembershipService implements IMembershipService {
  constructor(@inject(TYPES.ApiGateway) private readonly apiGateway: IApiGateway) {}

  private get endpointBase() {
    return `team/${this.apiGateway.company}/memberships`;
  }

  public async getMemberships(args?: GetMembershipArgs): Promise<HttpResponse> {
    const queryParams = this.apiGateway.createQueryParams(args);

    return await this.apiGateway.use('fluer').fetch('GET', `${this.endpointBase}?${queryParams}`);
  }

  public async getMembershipsCount(): Promise<HttpResponse> {
    return await this.apiGateway.use('fluer').fetch('GET', `${this.endpointBase}/count`);
  }

  public async inviteMember(body?: InviteMemberBody): Promise<HttpResponse[]> {
    const requests = body?.email.map((email) => {
      return this.apiGateway.use('fluer').fetch('POST', `${this.endpointBase}`, {
        email,
        access_level: body.access_level,
        extra_data: body.extra_data,
      });
    });

    return await Promise.all(requests || []);
  }

  public async resendInvite(id: string, invited_at?: string): Promise<HttpResponse> {
    return await this.apiGateway
      .use('fluer')
      .fetch('PUT', `${this.endpointBase}/${id}`, invited_at ? { invited_at } : undefined);
  }

  public async updateMemberRole(id: string, access_level: string): Promise<HttpResponse> {
    return await this.apiGateway
      .use('fluer')
      .fetch('PUT', `${this.endpointBase}/${id}`, { access_level });
  }

  public async validateInvitationToken(email: string, token: string): Promise<HttpResponse> {
    const queryParams = this.apiGateway.createQueryParams({ email });

    return await this.apiGateway
      .use('fluer')
      .fetch('GET', `${this.endpointBase}/validate/${token}?${queryParams}`);
  }

  public async removeMember(id: string): Promise<HttpResponse> {
    return await this.apiGateway.use('fluer').fetch('DELETE', `${this.endpointBase}/${id}`);
  }

  public async removeCollaborationFromProject(
    id: string,
    project_id: string
  ): Promise<HttpResponse> {
    return await this.apiGateway
      .use('fluer')
      .fetch('DELETE', `${this.endpointBase}/${id}/collaboration`, {
        project_id,
      });
  }
}
